import React, { useContext, useState, useMemo, useEffect } from 'react'
import { GridItem } from '@zuto/core/src/grid'
import * as yup from 'yup'
import MuiTextField from '@zuto/atoms/src/Mui/TextField/MuiTextField'
import debounce from 'lodash.debounce'
import { fireReactGAEvent, handlePrevButtonClick, questionFontStyle } from '../utils'
import AppFormQuestion from '../../AppFormQuestion'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import QuestionComponentProps from '../../../Questions/question-component-props'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'
import MobileNumberValidator from '../../../validators/MobileNumberValidator'
import { EmailAddressValidatorLookupService, displayClickableEmail } from '../../../validators/EmailAddressValidatorLookupService'
import { AttributionContext } from '../../../context'
import { Loading } from '@zuto/molecules'
import { useAppConfig } from '../../../hooks/useAppConfig'
import LookupService from '../../../services/lookup-service'
import EmailAddressValidator from '../../../validators/EmailAddressValidator'


type FieldValues = {
  contactNumber: string
  emailAddress: string
}

type FieldKey = keyof FieldValues
type ValidationSchemaKey = 'contactNumber' | 'emailAddress'


const ContactInfoQuestion = ({
  existingAnswers,
  disablePrevious,
  errors: existingErrors,
  bouncerError
}: QuestionComponentProps) => {
  const { dispatch, featureSwitches } = useContext(ApplicationFormContext)
  const attribution = useContext(AttributionContext)
  const appConfig = useAppConfig()
  const lookupService = LookupService(appConfig)
  const { bouncerV1 } = featureSwitches ?? {}
  const determinedFont = questionFontStyle
  const [loading, setLoading] = useState(false)
  const [, setFocus] = useState<FieldKey | 'nextButton' | 'previousButton' | null>(null)

  const [values, setValues] = useState<FieldValues>(existingAnswers ?? {
    contactNumber: '',
    emailAddress: ''
  })

  // An error shows once the field has been wrong for 1.5s (or straight away when the question is revisited with the
  // error still there). This records that the delay has passed; whether an error is on screen is derived below.
  const [errorDelayElapsed, setErrorDelayElapsed] = useState<{ [key in FieldKey]: boolean }>({
    contactNumber: !!existingErrors?.contactNumber && !!existingAnswers?.contactNumber,
    emailAddress: !!existingErrors?.emailAddress && !!existingAnswers?.emailAddress
  })

  const [dirtyFields, setDirtyFields] = useState<{ [key in FieldKey]: boolean }>({
    contactNumber: !!existingAnswers?.contactNumber,
    emailAddress: !!existingAnswers?.emailAddress
  })

  const [emailValidationError, setEmailValidationError] = useState<string>(bouncerError ?? '')

  const validationSchemas: {
    [key in ValidationSchemaKey]: yup.BaseSchema
  } = {
    contactNumber: MobileNumberValidator().getValidator(),
    emailAddress: bouncerV1 ? EmailAddressValidatorLookupService(emailValidationError).getValidator() : EmailAddressValidator().getValidator()
  }

  const validate = (key: ValidationSchemaKey, value: string): string => {
    try {
      validationSchemas[key].validateSync({ [key]: value })
    } catch (untypedError: unknown) {
      const errorObject = untypedError as yup.ValidationError
      return errorObject.message
    }
    return ''
  }

  const contactNumberError = useMemo(() => validate('contactNumber', values.contactNumber), [values.contactNumber])
  const emailAddressError = useMemo(() => validate('emailAddress', values.emailAddress), [values.emailAddress, emailValidationError])

  const errors = [contactNumberError, emailAddressError]

  const fieldHasError: { [key in FieldKey]: boolean } = {
    contactNumber: dirtyFields.contactNumber && !!contactNumberError,
    emailAddress: dirtyFields.emailAddress && !!(emailAddressError || emailValidationError)
  }

  const showErrors: { [key in FieldKey]: boolean } = {
    contactNumber: fieldHasError.contactNumber && errorDelayElapsed.contactNumber,
    emailAddress: fieldHasError.emailAddress && errorDelayElapsed.emailAddress
  }

  const debouncedSetShowError = (key: FieldKey) => {
    setErrorDelayElapsed((previous) => ({ ...previous, [key]: true }))
    fireReactGAEvent({
      category: 'Application Form',
      action: 'Validation Error',
      label: key,
      nonInteraction: true
    })
  }

  const debounceMap : Record<FieldKey, ReturnType<typeof debounce>> = useMemo(
    () => ({
      contactNumber: debounce(() => debouncedSetShowError('contactNumber'), 1500),
      emailAddress: debounce(() => debouncedSetShowError('emailAddress'), 1500)
    }),
    []
  )

  // Start the delay while a field is wrong and its error is not yet on screen; drop a pending one once it is right.
  useEffect(() => {
    if (fieldHasError.contactNumber && !showErrors.contactNumber) debounceMap.contactNumber()
    else debounceMap.contactNumber.cancel()
  }, [values.contactNumber, contactNumberError, dirtyFields.contactNumber, showErrors.contactNumber])
  useEffect(() => {
    if (fieldHasError.emailAddress && !showErrors.emailAddress) debounceMap.emailAddress()
    else debounceMap.emailAddress.cancel()
  }, [values.emailAddress, emailAddressError, dirtyFields.emailAddress, showErrors.emailAddress])

  // A field that is right now gets the full delay again the next time it is wrong
  const restartErrorDelayIfValid = (key: FieldKey) => {
    if (!fieldHasError[key]) setErrorDelayElapsed((previous) => (previous[key] ? { ...previous, [key]: false } : previous))
  }

  const displayNextButton: boolean = useMemo(
    () => Object.values(dirtyFields).every((isDirty) => isDirty) && errors.every((error) => !error),
    [dirtyFields, errors]
  )

  const onChange = (key: FieldKey, value: string) => {
    if (key === 'emailAddress' && bouncerV1) setEmailValidationError('')
    restartErrorDelayIfValid(key)
    if (!dirtyFields[key]) setDirtyFields((previous) => ({ ...previous, [key]: true }))
    setValues((previous) => ({ ...previous, [key]: value }))
  }

  const runDisplayClickableEmail = (emailAddressError: string) => displayClickableEmail(emailAddressError, onChange)

  const dispatchQuestionAnswered = () => {
    setEmailValidationError('')
    dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.CONTACT_INFO,
      value: values,
      errors: showErrors,
      ...(bouncerV1 && {bouncerError: ''})
    })
  }

  const nextQuestion = async () => {
    if (displayNextButton) {
      if (!bouncerV1) return dispatchQuestionAnswered()
      try {
        setLoading(true)
        await lookupService.validateEmail(values.emailAddress, attribution.source)
        dispatchQuestionAnswered()
      } catch(err) {
        const error = err as { message: string }
        setLoading(false)
        setEmailValidationError(error?.message)
        // the lookup's error shows at once, without the delay
        if (error?.message) setErrorDelayElapsed((previous) => ({ ...previous, emailAddress: true }))
        !(error?.message) && dispatchQuestionAnswered()
      }
    }
  }

  const previousQuestion = () => handlePrevButtonClick(dispatch, {
    name: QuestionKey.CONTACT_INFO,
    value: values,
    errors: showErrors,
    ...(bouncerV1 && {bouncerError: emailValidationError})
  })

  return (
    <>
      <GridItem gridColumn={['auto / span 12', '3 / span 8']} mb={5} minHeight="255px">
        <AppFormQuestion
          questionKey={QuestionKey.CONTACT_INFO}
          mt={3}
          mb={3}
          textStyle={determinedFont}
        >
          We&apos;ll keep you updated
        </AppFormQuestion>
        <MuiTextField
          onFocus={() => setFocus('contactNumber')}
          autoComplete="tel"
          data-hj-suppress=""
          defaultValue={existingAnswers?.contactNumber}
          errorMessage={showErrors.contactNumber ? contactNumberError : undefined}
          id="contactNumber"
          inputMaxLength={20}
          inputType="tel"
          inputVariant="outlined"
          label="Mobile number"
          mb="34px"
          multipleInputs
          name="contactNumber"
          onChangeHandler={(event) => onChange('contactNumber', event.currentTarget.value)}
          testId="contactNumber"
        />
        <MuiTextField
          onFocus={() => setFocus('emailAddress')}
          autoComplete="email"
          data-hj-suppress=""
          {... bouncerV1 ? 
            {value: values.emailAddress} // Controlled component
            : {defaultValue: existingAnswers?.emailAddress}}
          errorMessage={showErrors.emailAddress ? runDisplayClickableEmail(emailAddressError) : undefined}
          id="emailAddress"
          inputMaxLength={100}
          inputType="email"
          inputVariant="outlined"
          label="Email address"
          mb="34px"
          name="emailAddress"
          onChangeHandler={(event) => onChange('emailAddress', event.currentTarget.value)}
          testId="emailInput"
        />
        {loading && <Loading />}
      </GridItem>
      <NavigationButtons
        nextHandler={nextQuestion}
        previousHandler={previousQuestion}
        disableNext={!displayNextButton}
        disablePrevious={disablePrevious ? 'invisible' : undefined}
        nextButtonProps={{ onFocus: () => setFocus('nextButton') }}
        previousButtonProps={{ onFocus: () => setFocus('previousButton') }}
      />
    </>
  )
}

export default withFade(withQuestion(ContactInfoQuestion))
